import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import { useBilling } from "../../hooks/useBilling";
import { formatMoney, formatDate } from "../../views/Settings/Billing/formatters";

/**
 * Global band shown when the tenant's entitlement has dropped to `Restricted`
 * (writes blocked) or `Dormant` (writes blocked, sign-in narrowed).
 *
 * NOT `LockedShell`. That component is the Free-tier upsell brochure and it
 * says "here's what you could have". This says the opposite: "you already have
 * all of it, it is all still here, a payment lapsed". Reusing the brochure for
 * a lapsed paying customer would be both wrong and insulting.
 *
 * THE DATA PROMISE IS THE POINT. The single most likely reaction to a lockout
 * banner is "are they holding our payroll hostage?", and the answer is no and
 * has to be visible without clicking anything. Nothing is deleted, nothing is
 * hidden, export is never blocked at any state, and paying restores everything
 * with one write and no fee. That sentence sits in the banner body, not behind
 * a link.
 *
 * RBAC: mounted only for HR/Admin (the hook that feeds it is gated the same
 * way), because a plain employee must not learn that a billing surface exists.
 * An employee who hits the write block gets the translated `subscription_restricted`
 * toast from `requestHandling.ts` instead, which tells them to talk to HR and
 * offers no billing link.
 *
 * Visual: full border + tinted surface + leading icon. No side stripe, no
 * gradient, no glass. Amber reads as "act soon", red as "function is gone" —
 * the two states genuinely differ and the colour is the fastest way to say so.
 */

const SETTINGS_ROLES = ["Hr", "Admin"];

const RestrictedBanner: React.FC = () => {
  const { t, i18n } = useTranslation("billing");
  const navigate = useNavigate();
  const role = useSelector((s: { user?: { role?: string } }) => s.user?.role);
  const canManageBilling = SETTINGS_ROLES.includes(role || "");
  const { overview, accessState, isRestricted } = useBilling(canManageBilling);

  if (!canManageBilling || !isRestricted) return null;

  const isDormant = accessState === "Dormant";
  const banner = overview?.accessBanner ?? null;
  const currency = banner?.currency ?? overview?.currency ?? null;
  const amountDue = banner?.amountDueGross ?? null;
  const language = i18n.language;

  const tone = isDormant
    ? {
        surface: "border-destructive/40 bg-destructive/10",
        icon: "text-destructive",
        iconName: "ShieldAlert" as const,
      }
    : {
        surface: "border-amber-500/40 bg-amber-500/10",
        icon: "text-amber-700 dark:text-amber-400",
        iconName: "Lock" as const,
      };

  const since = banner?.restrictedAtUtc ?? banner?.graceEndedAtUtc ?? null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="billing-restricted-banner"
      className={`sticky top-0 z-30 -mx-4 -mt-4 mb-6 border-b px-4 py-3 md:-mx-6 md:-mt-6 md:px-6 ${tone.surface}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Icon name={tone.iconName} size={18} className={`mt-0.5 shrink-0 ${tone.icon}`} />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {isDormant ? t("restricted.dormantTitle") : t("restricted.title")}
            </p>
            <p className="max-w-[70ch] text-sm text-muted-foreground">
              {amountDue != null
                ? t("restricted.bodyWithAmount", {
                    amount: formatMoney(amountDue, currency, language),
                    date: formatDate(since, language),
                  })
                : t("restricted.body")}
            </p>
            <p className="max-w-[70ch] text-sm font-medium text-foreground">
              {t("restricted.dataPromise")}
            </p>
          </div>
        </div>

        <Button
          variant="default"
          size="sm"
          className="shrink-0 self-start"
          onClick={() => navigate("/settings/billing")}
          iconName="CreditCard"
        >
          {t("restricted.payNow")}
        </Button>
      </div>
    </div>
  );
};

export default RestrictedBanner;
